import _ from 'lodash';
import _isNil from 'lodash/isNil';
import _orderBy from 'lodash/orderBy';
import _times from 'lodash/times';
// eslint-disable-next-line import/no-extraneous-dependencies
import Big from 'big.js';
import { getProductWeight } from 'shared/helpers/products';

// Adds pricing adjustment data to the passed-in adjustments object
const addAdjustments = ({ adjustments, allFlowerDetailsHash, possibleAdjustments, targetWeight, tierId }) => {
  _.forEach(_.keys(possibleAdjustments[tierId][targetWeight].items), (itemKey) => {
    const { weight } = allFlowerDetailsHash[itemKey];
    const { tierUnitPrice } = possibleAdjustments[tierId][targetWeight];
    const adjustedBasePrice = tierUnitPrice.times(weight);
    const quantityDiscounted = possibleAdjustments[tierId][targetWeight].items[itemKey];
    const discountAmount = adjustments[itemKey]?.discountAmount || 0;
    const totalDiscountAmountForDetail = Big(discountAmount).add(
      Big(allFlowerDetailsHash[itemKey].basePrice)
        .times(quantityDiscounted)
        .minus(adjustedBasePrice.times(quantityDiscounted))
    );

    if (totalDiscountAmountForDetail.gt(0)) {
      adjustments[itemKey] = {
        discountAmount: totalDiscountAmountForDetail,
        quantity: (adjustments[itemKey]?.quantity || 0) + quantityDiscounted,
      };
    }
  });
};

// Given tiers data and the targetWeight, returns the gram unit price for the tier in dollars
export const getTierUnitPrice = (detail, targetWeight) => {
  const matchingTier = _.find(detail.pricingTierData?.tiers || [], (tier) => {
    const weightUOM = tier.weightUOM === 'each' ? '' : tier.weightUOM;
    const lower = _isNil(tier.startWeight) ? -Infinity : getProductWeight(`${tier.startWeight}${weightUOM}`);
    const upper = _isNil(tier.endWeight) ? Infinity : getProductWeight(`${tier.endWeight}${weightUOM}`);
    // in this case a tier has a specific value and not a range
    if (lower === upper) {
      return targetWeight === lower;
    }
    // if it's a range, we check if the target weight is in the range, lower bound inclusive, upper bound exclusive
    return targetWeight >= lower && targetWeight < upper;
  });

  if (matchingTier) {
    const pricePerGram = !_isNil(matchingTier.pricePerGram)
      ? Big(matchingTier.pricePerGram)
      : Big(matchingTier.price || 0).div(targetWeight);
    // Need to divide by basePriceMult first to account for POS taxes
    return pricePerGram.div(detail.basePriceMult).div(100);
  }
  return Big(detail.basePrice || 0); // Return regular basePrice if no tier was matched
};

// Determines mix and match pricing adjustments and adds mixAndMatch data to provided details
export const determineMixAndMatchAdjustments = (allDetails, dispensary, isDelivery) => {
  if (isDelivery && !dispensary?.storeSettings?.enableMixAndMatchPricingForDelivery) {
    return allDetails;
  }

  if (!isDelivery && !dispensary?.storeSettings?.enableMixAndMatchPricingForPickup) {
    return allDetails;
  }

  const allFlowerDetails = _orderBy(
    _.filter(allDetails, ['productCategory', 'Flower']),
    (detail) => getProductWeight(detail.option),
    'desc'
  );
  const allFlowerDetailsHash = _.reduce(
    allFlowerDetails,
    (obj, detail) => {
      obj[detail.key] = { ...detail, weight: getProductWeight(detail.option) };
      return obj;
    },
    {}
  );

  const mixAndMatchWeights = _orderBy(
    _.map(dispensary?.mixAndMatchPricingWeights || [], (weight) => getProductWeight(weight)),
    [],
    'desc'
  );

  const adjustments = {};
  const possibleAdjustments = {};

  _.forEach(mixAndMatchWeights, (targetWeight) => {
    // eslint-disable-next-line consistent-return
    _.forEach(allFlowerDetails, (detail) => {
      const tierId = detail.pricingTierData?.tiersId || detail.pricingTierData?.generatedTiersId;
      if (!tierId) {
        return false;
      }

      // create the mapping that we use later to tally the appropriate adjustments
      if (!possibleAdjustments[tierId]) {
        possibleAdjustments[tierId] = {};
      }
      if (!possibleAdjustments[tierId][targetWeight]) {
        possibleAdjustments[tierId][targetWeight] = {
          items: {},
          remainingWeightUntilDiscount: targetWeight,
          tierUnitPrice: getTierUnitPrice(detail, targetWeight),
        };
      }

      const detailKey = detail.key;
      const detailWeight = allFlowerDetailsHash[detailKey].weight;

      if (detailWeight < targetWeight) {
        _times(Math.max(detail.quantity - (adjustments[detailKey]?.quantity || 0), 0), () => {
          let remainingWeight = possibleAdjustments[tierId][targetWeight].remainingWeightUntilDiscount;
          remainingWeight -= detailWeight;

          if (remainingWeight >= 0) {
            // tally the possible adjustments for each tier, aiming to hit remainingWeight === 0
            if (!possibleAdjustments[tierId][targetWeight].items[detailKey]) {
              possibleAdjustments[tierId][targetWeight].items[detailKey] = 1;
            } else {
              possibleAdjustments[tierId][targetWeight].items[detailKey] += 1;
            }

            // if the remaining weight is 0 we know to apply the adjustments
            if (remainingWeight === 0) {
              addAdjustments({ adjustments, allFlowerDetailsHash, possibleAdjustments, targetWeight, tierId });
              possibleAdjustments[tierId][targetWeight].items = {};
              possibleAdjustments[tierId][targetWeight].remainingWeightUntilDiscount = targetWeight;
            } else {
              possibleAdjustments[tierId][targetWeight].remainingWeightUntilDiscount = remainingWeight;
            }
          }
        });
      }
    });
  });

  _.forEach(allFlowerDetails, (detail) => {
    const detailKey = detail.key;
    if (adjustments[detailKey]) {
      const discountAmount = Number(adjustments[detailKey].discountAmount.round(2));
      detail.mixAndMatch = {
        adjustedBasePrice: Big(detail.basePrice).times(detail.quantity).minus(discountAmount).div(detail.quantity),
        discountAmount,
      };
    }
  });

  return allDetails;
};
